import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useState } from 'react';

import {
  type EmployeesOfCompanyPayload,
  fetchEmployeesOfCompany,
  queryKeysCompany,
} from '~/data/company';
import { useQueryDeliveryNoteChains } from '~/data/deliveryNote';
import {
  fetchUser,
  fetchUserCompany,
  fetchUserCompanyAccount,
  queryKeysUser,
} from '~/data/user';

import { clone } from '@vestigas/utils';
import { promiseAllThrottled } from '~/utils/promise';

import { type DeliveryNoteCreatedValues } from '../DeliveryNote';
import { DeliveryNoteActionObject } from '../deliveryNoteActionUtils';

export const useDeliveryNoteActions = (
  deliveryNote: DeliveryNoteCreatedValues | undefined,
) => {
  const [deliveryNoteActions, setDeliveryNoteActions] = useState<any[]>([]);

  const {
    data: deliveryNoteChainsData,
    isError: isErrorDeliveryNoteChains,
    isLoading: isLoadingDeliveryNoteChains,
  } = useQueryDeliveryNoteChains([deliveryNote?.id], {
    enabled: Boolean(deliveryNote?.id),
  });

  const queryClient = useQueryClient();

  const loadDeliveryNoteHistory = useCallback(
    async (deliveryNote: DeliveryNoteCreatedValues) => {
      const newDln = clone(deliveryNote);

      const newDeliveryNoteActions = await promiseAllThrottled(
        deliveryNote.assetChain?.map(async (chainId, index) => {
          const chain = deliveryNoteChainsData?.find(
            ({ id }) => id === chainId,
          );

          if (!chain) {
            return null;
          }

          const newDeliveryNoteAction = DeliveryNoteActionObject.create(
            chain,
            newDln,
          );

          if (
            newDeliveryNoteAction.action?.includes(
              DeliveryNoteActionObject.ACTION.CREATED.STRING,
            ) &&
            deliveryNote.submittedByUserId &&
            deliveryNote.submittedByUser
          ) {
            newDeliveryNoteAction.user = deliveryNote.submittedByUser;
            newDeliveryNoteAction.source = deliveryNote.source;
          }

          // Initialize history first
          newDln.addChainToHistory(
            chain,
            newDeliveryNoteAction.company?.name,
            index === 0,
          );

          newDln.attachmentHandler.addAttachmentsFromChain(chain);
          const newAttachment = newDln.attachmentHandler.attachments;
          newDeliveryNoteAction.attachments.push(newAttachment);

          const userId = newDeliveryNoteAction.user?.id;

          // Initialize user and company after history
          await newDeliveryNoteAction.initCompanyWithReactQueryFn(async () =>
            queryClient.fetchQuery({
              queryFn: async () => fetchUserCompany(userId),
              queryKey: queryKeysUser.getCompany(userId),
            }),
          );

          await newDeliveryNoteAction.initUserWithReactQueryFn(
            async () =>
              queryClient.fetchQuery({
                queryFn: async () => fetchUser(userId),
                queryKey: queryKeysUser.get(userId),
              }),
            async () =>
              queryClient.fetchQuery({
                queryFn: async () => fetchUserCompany(userId),
                queryKey: queryKeysUser.getCompany(userId),
              }),
            async () =>
              queryClient.fetchQuery({
                queryFn: async () => fetchUserCompanyAccount(),
                queryKey: queryKeysUser.getCompanyAccount(),
              }),
            async (companyPayload: EmployeesOfCompanyPayload) =>
              queryClient.fetchQuery({
                queryFn: async () => fetchEmployeesOfCompany(companyPayload),
                queryKey: queryKeysCompany.getEmployees(companyPayload),
              }),
          );

          newDln.changes = [];
          newDeliveryNoteAction.setChanges(newDln);

          return newDeliveryNoteAction;
        }) || [],
      );

      const filteredNewDeliveryNoteAction =
        newDeliveryNoteActions.filter(Boolean);

      // Important: Merge history after all chains have been processed
      newDln.mergeHistory();

      // Write the chain ids of the attachments from the attachment handler
      newDln.attachmentHandler.populateAcceptArticleChainIds(newDln);
      newDln.attachmentHandler.populateDeliveryNoteActions(
        filteredNewDeliveryNoteAction,
      );
      setDeliveryNoteActions(filteredNewDeliveryNoteAction);

      // Update the delivery note data in React Query cache
      queryClient.setQueryData(['deliveryNote', deliveryNote?.id], () => {
        // Instead of creating a new instance, update the existing one
        const updatedDeliveryNote = deliveryNote;

        // Copy over the merged history and other properties
        updatedDeliveryNote.history = newDln.history;
        updatedDeliveryNote.recalculatedMain = newDln.recalculatedMain;
        updatedDeliveryNote.attachmentHandler = newDln.attachmentHandler;
        updatedDeliveryNote.customData = newDln.customData;

        // Ensure ValueGroups are properly merged
        updatedDeliveryNote.mergeHistory();

        return updatedDeliveryNote;
      });
    },
    [deliveryNoteChainsData],
  );

  useEffect(() => {
    if (deliveryNote && deliveryNoteChainsData?.length > 0) {
      loadDeliveryNoteHistory(deliveryNote);
    }
  }, [deliveryNote, deliveryNoteChainsData?.length, loadDeliveryNoteHistory]);

  return {
    deliveryNoteActions,
    isErrorDeliveryNoteChains,
    isLoadingDeliveryNoteChains,
  };
};
